import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import ChatWidget from "@/components/ChatWidget";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import Editable from "@/components/Editable";
import api from "@/lib/api";
import { ArrowRight, ClockCounterClockwise, ArticleMedium } from "@phosphor-icons/react";

export default function Tips() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        title="Training Tips & Articles | Puppl"
        description="Free puppy training tips from Sunshine Coast trainer the Puppl team — loose leash, recall, safe socialisation, settling on bed and more."
        canonical={typeof window !== "undefined" ? window.location.origin + "/tips" : ""}
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Puppl Training Tips",
            "url": "https://puppl.app/tips",
            "publisher": { "@id": "https://puppl.app/#org" },
          },
          breadcrumbSchema([
            { name: "Home", url: "https://puppl.app/" },
            { name: "Tips", url: "https://puppl.app/tips" },
          ]),
        ]}
      />
      <PublicHeader />

      <section className="bg-[#1b4a76] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20 text-center">
          <Editable id="tips.eyebrow" as="p" className="text-sm uppercase tracking-wider font-semibold text-[#E2B054] mb-3" defaultHtml="Training tips" />
          <Editable id="tips.heading" as="h1" className="font-heading text-4xl lg:text-6xl font-bold leading-tight mb-3" defaultHtml="Real-world puppy advice." />
          <Editable id="tips.subtitle" as="p" className="text-lg lg:text-xl opacity-90" defaultHtml="Short reads from the team — practical, science-backed, non-aversive." />
        </div>
      </section>

      <section className="py-16 px-6 lg:px-12 max-w-6xl mx-auto">
        {posts.length === 0 ? (
          <div className="card-pp p-16 text-center text-stone-500" data-testid="tips-empty">
            <ArticleMedium size={48} weight="duotone" className="mx-auto mb-3 text-stone-300" />
            New articles coming soon. <Link to="/contact" className="text-[#f38838] font-semibold">Get notified</Link>.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {posts.map((p) => (
              <Link to={`/tips/${p.slug}`} key={p.id} className="card-pp overflow-hidden hover:-translate-y-1 transition-transform" data-testid={`tip-${p.slug}`}>
                {p.cover_image && (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img loading="lazy" src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3 text-xs">
                    <span className="bg-[#f38838]/15 text-[#f38838] font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider">{p.category}</span>
                    <span className="text-stone-500 flex items-center gap-1"><ClockCounterClockwise size={12} /> {p.read_minutes} min read</span>
                  </div>
                  <h2 className="font-heading font-semibold text-xl text-stone-900 mb-2 leading-snug">{p.title}</h2>
                  <p className="text-sm text-stone-600 mb-4 leading-relaxed">{p.excerpt}</p>
                  <span className="text-sm font-semibold text-[#f38838] inline-flex items-center gap-1">
                    Read more <ArrowRight size={14} weight="bold" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-12 bg-stone-100 rounded-2xl p-8 text-center">
          <Editable id="tips.cta.body" as="p" className="text-stone-700 mb-3" defaultHtml="Want personalised tips for your puppy?" />
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
            <Editable id="tips.cta.button" inline as="span" defaultHtml="Book a Free Consult" /> <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      <PublicFooter />
      <ChatWidget />
    </div>
  );
}
